import { loadAccountData } from 'store/customer/customer-view-actions';
import { moveSelected } from 'utils/utils';
import isEmpty from 'lodash/isEmpty';
import Affiliations from '../affiliations/Affiliations';
import SiteAffiliations from '../affiliations/SiteAffiliation';
import Engagements from '../engagements/Engagements';
import Insights from '../insights/Insights';
import PayerMix from '../payer-mix/PayerMix';
import ProductMix from '../product-mix/ProductMix';
import SitePerformance from '../site-performance/SitePerformance';
import SiteUtilization from '../site-utilization/SiteUtilization';
import MenuItem from './MenuItem';
import PolicyDetails from '../mco/policy-detail/PolicyDetails';
import CopayAnalytics from '../copay-analytics/CopayAnalytics';
import EcosystemOpportunity from './../mco/ecosystem-opportunity/EcosystemOpportunity';
import { ACCOUNT_TYPE, COMPONENT_MAPPING } from 'constants/constants';

export const loadOptions = async (
  offset,
  options,
  selected,
  type,
  ecosystem,
  prevEcosystem,
  searchQuery,
  loadMore,
  unixId,
  isMyPriority,
  dropdownAction,
  searchOptions
) => {
  let data = [];
  let hasMore = false;
  let listSize = 0;

  /**
    1.use the existing option list if
    user made a selection, is not search,
    ecosystem has not changed and hasn't reach the dropdown height.
    2. use the existing search option list if
    user made a selection, is search,
    ecosystem has not changed and hasn't reach the dropdown height.
  **/
  if (
    selected?.length > 0 &&
    !searchQuery &&
    ecosystem?.toString() === prevEcosystem?.toString() &&
    options?.length > 0 &&
    !loadMore
  ) {
    data = [...move(selected, options)];
  } else if (
    selected?.length > 0 &&
    ecosystem?.toString() === prevEcosystem?.toString() &&
    searchOptions.length > 0 &&
    !loadMore &&
    searchQuery &&
    dropdownAction === 'set-value'
  ) {
    data = [...move(selected, searchOptions)];
  } else {
    let { values, hasMoreElement, currentListSize } = await getData(
      type,
      ecosystem,
      offset,
      searchQuery,
      selected,
      unixId,
      isMyPriority,
      loadMore
    );

    hasMore = hasMoreElement;
    data = [...values];
    listSize = currentListSize;
  }

  return {
    data: data,
    hasMore: hasMore,
    currentListSize: listSize,
  };
};

export const getData = async (
  type,
  ecosystem,
  offset,
  searchQuery,
  selected,
  unixId,
  isMyPriority,
  loadmore
) => {
  let data = [];

  const { values, currentListSize, hasMoreElement } = await loadAccountData({
    offset: offset,
    searchToken: searchQuery,
    filed: type,
    ecosystems: ecosystem,
    unixId,
    isMyPriority,
  });

  // move the selected options to the top of the list
  if ((selected?.length > 0 || !isEmpty(selected)) && !loadmore) {
    const opts = moveSelected(selected, values);
    data = [...opts];
  } else {
    data = [...values];
  }

  return { values: data, currentListSize, hasMoreElement };
};

export const shouldLoadMoreOptions = (
  scrollHeight,
  clientHeight,
  scrollTop
) => {
  const bottomBorder = (scrollHeight - clientHeight) / 2;
  return bottomBorder < scrollTop;
};

const move = (selected, options) => {
  if (selected?.length > 0 && options?.length > 0) {
    const opts = moveSelected(selected, options);
    return opts;
  } else if (options.length > 0) {
    return options;
  }

  return options;
};

export const getSelectedDimension = (dimensions, selectedDimensionName) => {
  let defaultDimension = dimensions?.filter(
    (dm) => dm.dimensionName === selectedDimensionName
  )?.[0];
  return defaultDimension;
};

export const searchResult = (result = [], searchText, field = 'label') => {
  const options = result?.filter((item) =>
    item[field]?.toLowerCase()?.includes(searchText?.toLowerCase())
  );
  return options;
};

export const getHCOMenuItems = (selectedView, componentAccessList, roleId) => [
  {
    key: COMPONENT_MAPPING.Insights, // Key and activeSectionTab value is same to be used to restrict data fetching in each component based on active tab
    shouldRender: true,
    component: <Insights />,
    label: (
      <MenuItem
        href='#Insights' // href and id from each component are in synck to scroll up the page on tab click, default behavior of anchor tag
        label='Insights'
        id={COMPONENT_MAPPING.Insights}
      />
    ),
  },
  {
    key: COMPONENT_MAPPING.Engagements,
    shouldRender: true,
    component: <Engagements />,
    label: (
      <MenuItem
        href='#Engagements'
        label='Engagements'
        id={COMPONENT_MAPPING.Engagements}
      />
    ),
  },
  {
    key: COMPONENT_MAPPING.SitePerformance,
    shouldRender:
      selectedView === ACCOUNT_TYPE.account &&
      componentAccessList?.includes(COMPONENT_MAPPING.SitePerformance),
    component: <SitePerformance />,
    label: (
      <MenuItem
        href='#SitePerformance'
        label='Site Performance'
        id={COMPONENT_MAPPING.SitePerformance}
      />
    ),
  },
  {
    key: COMPONENT_MAPPING.ProductMix,
    shouldRender:
      selectedView === ACCOUNT_TYPE.site &&
      componentAccessList?.includes(COMPONENT_MAPPING.ProductMix),
    component: <ProductMix />,
    label: (
      <MenuItem
        href='#ProductMix'
        label='Product Mix'
        id={COMPONENT_MAPPING.ProductMix}
      />
    ),
  },
  {
    key: COMPONENT_MAPPING.PayerMix,
    shouldRender: componentAccessList?.includes(COMPONENT_MAPPING.PayerMix),
    component: <PayerMix />,
    label: (
      <MenuItem
        href='#PayerMix'
        label='Payer Mix'
        id={COMPONENT_MAPPING.PayerMix}
      />
    ),
  },
  {
    key: COMPONENT_MAPPING.SiteUtilization,
    shouldRender: componentAccessList?.includes(
      COMPONENT_MAPPING.SiteUtilization
    ),
    component: <SiteUtilization />,
    label: (
      <MenuItem
        href='#SiteUtilization'
        label='Site Utilization & Demographics'
        id={COMPONENT_MAPPING.SiteUtilization}
      />
    ),
  },
  {
    key: COMPONENT_MAPPING.Affiliations,
    shouldRender: selectedView === ACCOUNT_TYPE.account,
    component: (
      <Affiliations
        data={selectedView}
        role={roleId}
      />
    ),
    label: (
      <MenuItem
        href='#Affiliations'
        label='Affiliations'
        id={COMPONENT_MAPPING.Affiliations}
      />
    ),
  },
  {
    key: COMPONENT_MAPPING.Affiliations,
    shouldRender: selectedView === ACCOUNT_TYPE.site,
    component: (
      <SiteAffiliations
        data={selectedView}
        role={roleId}
        filterExp=''
        enableTitle={true}
        tableType={'siteView'}
      />
    ),
    label: (
      <MenuItem
        href='#SiteAffiliations'
        label='Affiliations'
        id={COMPONENT_MAPPING.Affiliations}
      />
    ),
  },
  {
    key: COMPONENT_MAPPING.CopayAnalytics,
    shouldRender: componentAccessList?.includes(
      COMPONENT_MAPPING.CopayAnalytics
    ),
    component: <CopayAnalytics />,
    label: (
      <MenuItem
        href='#CopayAnalytics'
        label='Copay Analytics'
        id={COMPONENT_MAPPING.CopayAnalytics}
      />
    ),
  },
];

export const getMCOMenuItems = (selectedView, componentAccessList, roleId) => [
  {
    key: COMPONENT_MAPPING.PolicyDetailsParentPayer,
    shouldRender: selectedView === 'parent payer',
    component: (
      <PolicyDetails
        data={selectedView}
        role={roleId}
      />
    ),
    label: (
      <MenuItem
        href='#PolicyDetails'
        label='Policy Details'
        id={COMPONENT_MAPPING.PolicyDetailsParentPayer}
      />
    ),
  },
  {
    // Note : Sperated section menu items with same componet just to toggle PolicyDetails compoent to its default state while switching b/w payer customers
    key: COMPONENT_MAPPING.PolicyDetailsPayer,
    shouldRender: selectedView === 'payer',
    component: (
      <PolicyDetails
        data={selectedView}
        role={roleId}
      />
    ),
    label: (
      <MenuItem
        href='#PolicyDetails'
        label='Policy Details'
        id={COMPONENT_MAPPING.PolicyDetailsPayer}
      />
    ),
  },
  {
    key: COMPONENT_MAPPING.EcosystemOpportunity,
    shouldRender: componentAccessList?.includes(
      COMPONENT_MAPPING.EcosystemOpportunity
    ),
    component: <EcosystemOpportunity />,
    label: (
      <MenuItem
        href='#ecosystemOpportunity'
        label='Ecosystem Opportunity'
        id={COMPONENT_MAPPING.EcosystemOpportunity}
      />
    ),
  },
];
